
import { PropType } from "vue";
import { Vue, Options } from "vue-class-component";
import formatPrice from "@/app/infrastructures/misc/common-library/FormatPrice";
import { BalanceController } from "@/app/ui/controllers/BalanceController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { BeneficiaryAccountController } from "@/app/ui/controllers/BeneficiaryAccountController";
import { AuthController } from "@/app/ui/controllers/AuthController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { Snackbar } from "@/domain/entities/MainApp";
// import { FlagsPermissionSaldo } from "@/feature-flags/flags-permission-saldo";
import { FlagsPermissionCodBalance } from "@/feature-flags/flags-permission-cod-balance";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { FlagsOtp } from "@/feature-flags/flags-otp";
import { recaptchaV3 } from "@/app/infrastructures/misc/common-library/RecaptchaV3";
import { OtpRequest } from "@/data/payload/api/AuthRequest";
import { WithdrawBalanceRequest } from "@/data/payload/api/BalanceRequest";
import OtpDisclaimer from "@/app/ui/components/modal-otp/otp-disclaimer.vue";

@Options({
  components: {
    OtpDisclaimer
  },
  props: {
    /** Dipanggil saat tombol X atau "Cancel" diklik, dan setelah submit berhasil. */
    onClose: {
      default: Function as PropType<() => void>,
      type: Function
    },
    /**
     * Nominal penarikan yang SUDAH diisi & divalidasi di step sebelumnya
     * (GENESIS-20564). Sidebar ini hanya menampilkannya read-only.
     */
    amount: {
      default: 0,
      type: Number
    },
    /**
     * Password Genesis yang SUDAH divalidasi di step sebelumnya.
     * Dipakai untuk request OTP & submit withdraw, bukan untuk validasi ulang.
     */
    password: {
      default: "",
      type: String
    },
    /**
     * Hasil validasi password dari step sebelumnya — dipakai untuk
     * menentukan enabled/disabled tombol "Tarik saldo" di sidebar ini.
     */
    isPasswordValid: {
      default: false,
      type: Boolean
    },
    isAmountError: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    isVisibleRequest() {
      return FlagsPermissionCodBalance.permission_cod_balance_withdraw_request_detail.isEnabled();
    }
  }
})
class SidebarSubmitWithdraw extends Vue {
  props: any = this.$props;

  created() {
    // NOTE: sesuai ticket, data ini idealnya sudah di-fetch di level halaman
    // Withdraw dedicated saat load (sama seperti modal-withdraw.vue:412-420).
    // Panggilan di sini dijaga idempotent-nya oleh controller/cache; hapus
    // baris ini kalau halaman parent sudah pasti memanggilnya duluan.
    this.getBeneficiaryAccount();
  }

  isModalOtp = false;
  tokenFromOTP = "";
  otp = "";
  emailUser = "";
  phoneUser = "";
  errorMessageOtp = "";

  get countryCode() {
    const code = AccountController.accountData?.countryCode;
    return code?.toUpperCase() ?? "ID";
  }

  formatPrice(val: any) {
    const currency = this.countryCode === "MY" ? "MYR" : "IDR";
    return formatPrice(val, "", currency);
  }

  get isDisabled() {
    return (
      !this.props.isPasswordValid ||
      !this.props.amount ||
      this.beneficiaryModelValue === "-" ||
      this.props.isAmountError
    );
  }

  // get beneficiary account — data source sesuai FR-FE-15
  async getBeneficiaryAccount() {
    BeneficiaryAccountController.getBeneficiaryAccList({
      page: 1,
      limit: 10,
      bankAccType: "beneficiary",
      actortype: "",
      actorId: ""
    });
  }
  get isLoadingBeneficiary() {
    return BeneficiaryAccountController.isLoading;
  }
  get beneficiary() {
    return BeneficiaryAccountController.beneficiaryDetail;
  }
  get hasBeneficiary() {
    return !!this.beneficiary.bankAccountNumber;
  }
  get beneficiaryModelValue() {
    if (this.hasBeneficiary) {
      return `${this.beneficiary.bankAccountName} - ${this.beneficiary.bankAccountNumber}`;
    }
    return "-";
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  FlagsOtp = FlagsOtp;
  onSubmitWithdraw() {
    if (this.isDisabled) return;

    // if (this.isRolloutOn) {
    //   this.onCheckCaptchaAndValidAccount();
    //   return;
    // }

    this.onCallWithdraw();
  }

  onUpdateOpenOtp(value: boolean) {
    this.isModalOtp = value;
  }

  onVerify(otp: string, token: string) {
    this.onCallWithdraw(otp, token);
    this.isModalOtp = false;
  }

  actionRequestOtp = "withdraw_cod";

  async onCheckCaptchaAndValidAccount() {
    const action = "otp_enable_wd_cod";
    try {
      const captchaToken = await recaptchaV3.executeRecaptcha(action);
      MainAppController.showLoading();
      const res = await AuthController.requestOtp(
        new OtpRequest({
          password: this.props.password,
          captchaToken,
          action: "otp_enable_wd_cod"
        })
      );

      this.tokenFromOTP = res.token;
      this.emailUser = res.emailPartnerClient;
      this.phoneUser = res.phoneNumberPartnerClient;
      this.onUpdateOpenOtp(true);
      this.errorMessageOtp = "";
    } catch (error) {
      this.errorMessageOtp = (error as any)?.response?.data?.message?.id;
    } finally {
      MainAppController.closeLoading();
    }
  }

  async onCallWithdraw(otp = "", token = "") {
    if (this.isDisabled) {
      return;
    }
    const response = await BalanceController.withdrawBalance(
      new WithdrawBalanceRequest({
        amount: this.props.amount,
        version: AccountController.accountData.isPosAccount
          ? FlagsMedusa.config_version_endpoint_withdraw_pos.getValue()
          : "v1",
        token: token || this.tokenFromOTP || "",
        otp: otp || "",
        password: this.props.password
      })
    );
    if (response) {
      this.props.onClose();
      MainAppController.setSnackbarData(
        new Snackbar({
          title: this.$t("codIncome.withdrawal.snackbarSuccessTitle"),
          message: this.$t("codIncome.withdrawal.snackbarSuccessMessage", {
            amount: this.formatPrice(this.props.amount)
          }),
          type: "success"
        })
      );
      MainAppController.setSnackbar(true);
      await BalanceController.getBalance(true);
    }
  }

  onSubmitSuccessModal() {
    MainAppController.closeMessageModal();
    this.props.onClose();
  }

  get isRolloutOn() {
    const isCodWithOTP = FlagsOtp.otp_enable_wd_cod.isEnabled();

    return isCodWithOTP;
  }
}
export default SidebarSubmitWithdraw;
